import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Target, Loader2, CheckCircle2, Circle, Plus, X,
  Rocket, TrendingUp, TrendingDown, Sparkles, AlertTriangle
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { DictationButton } from '../components/DictationButton';
import BottomNav from '../components/BottomNav';

// Orbit parameter configurations
const ORBIT_PARAM_CONFIG = {
  vision: {
    icon: '🎯',
    color: '#8B5CF6',
    en: { name: 'Vision', description: 'Clarity of project goals and end state', question: 'What does success look like?' },
    tr: { name: 'Vizyon', description: 'Proje hedeflerinin ve son durumun netliği', question: 'Başarı nasıl görünüyor?' }
  },
  planning: {
    icon: '📋',
    color: '#3B82F6',
    en: { name: 'Planning', description: 'Quality of roadmap and task breakdown', question: 'What are the next 3 key milestones?' },
    tr: { name: 'Planlama', description: 'Yol haritası ve görev ayrıştırma kalitesi', question: 'Sonraki 3 önemli kilometre taşı nedir?' }
  },
  execution: {
    icon: '⚡',
    color: '#F59E0B',
    en: { name: 'Execution', description: 'Ability to deliver on commitments', question: 'What shipped this week?' },
    tr: { name: 'Uygulama', description: 'Taahhütleri yerine getirme yeteneği', question: 'Bu hafta ne teslim edildi?' }
  },
  team_energy: {
    icon: '🔥',
    color: '#EF4444',
    en: { name: 'Team Energy', description: 'Collective motivation and enthusiasm', question: 'How is team morale?' },
    tr: { name: 'Takım Enerjisi', description: 'Kolektif motivasyon ve coşku', question: 'Takım morali nasıl?' }
  },
  resources: {
    icon: '💰',
    color: '#10B981',
    en: { name: 'Resources', description: 'Budget, tools, and support available', question: 'What resources are missing?' },
    tr: { name: 'Kaynaklar', description: 'Bütçe, araçlar ve mevcut destek', question: 'Hangi kaynaklar eksik?' }
  },
  communication: {
    icon: '💬',
    color: '#06B6D4',
    en: { name: 'Communication', description: 'Information flow and alignment', question: 'Is everyone on the same page?' },
    tr: { name: 'İletişim', description: 'Bilgi akışı ve uyum', question: 'Herkes aynı sayfada mı?' }
  },
  momentum: {
    icon: '🚀',
    color: '#D4AF37',
    en: { name: 'Momentum', description: 'Speed and consistency of progress', question: 'What is accelerating or slowing us?' },
    tr: { name: 'Momentum', description: 'İlerlemenin hızı ve tutarlılığı', question: 'Bizi hızlandıran veya yavaşlatan nedir?' }
  },
  obstacles: {
    icon: '🧱',
    color: '#64748B',
    en: { name: 'Obstacles', description: 'Blockers and challenges (lower is better)', question: 'What is the biggest blocker right now?' },
    tr: { name: 'Engeller', description: 'Engelleyiciler ve zorluklar (düşük daha iyi)', question: 'Şu anda en büyük engel nedir?' }
  },
  leadership: {
    icon: '👑',
    color: '#A855F7',
    en: { name: 'Leadership', description: 'Decision making and direction', question: 'Are decisions being made quickly?' },
    tr: { name: 'Liderlik', description: 'Karar verme ve yönlendirme', question: 'Kararlar hızlı alınıyor mu?' }
  },
  creativity: {
    icon: '💡',
    color: '#EC4899',
    en: { name: 'Creativity', description: 'Innovation and problem-solving', question: 'What creative solutions have emerged?' },
    tr: { name: 'Yaratıcılık', description: 'Yenilik ve problem çözme', question: 'Hangi yaratıcı çözümler ortaya çıktı?' }
  },
  discipline: {
    icon: '🎖️',
    color: '#14B8A6',
    en: { name: 'Discipline', description: 'Focus and follow-through', question: 'Are we staying focused on priorities?' },
    tr: { name: 'Disiplin', description: 'Odak ve takip', question: 'Önceliklere odaklı kalıyor muyuz?' }
  },
  progress: {
    icon: '📈',
    color: '#22C55E',
    en: { name: 'Progress', description: 'Overall completion percentage', question: 'What percentage complete are we?' },
    tr: { name: 'İlerleme', description: 'Genel tamamlanma yüzdesi', question: 'Yüzde kaç tamamlandık?' }
  }
};

const OrbitMissionControl = () => {
  const { orbitId, paramKey } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [orbit, setOrbit] = useState(null);
  const [missions, setMissions] = useState([]);
  const [showAddMission, setShowAddMission] = useState(false);
  const [newMissionText, setNewMissionText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');

  const paramConfig = ORBIT_PARAM_CONFIG[paramKey] || ORBIT_PARAM_CONFIG.vision;
  const paramInfo = paramConfig[language] || paramConfig.en;
  const currentScore = orbit?.orbit_scores?.[paramKey] || 5;

  useEffect(() => {
    fetchOrbitData();
    fetchParamMissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orbitId, paramKey]);

  const fetchOrbitData = async () => {
    try {
      const response = await api.get(`/orbit/${orbitId}`);
      setOrbit(response.data);
    } catch (error) {
      toast.error('Failed to load orbit');
      navigate('/orbit');
    } finally {
      setLoading(false);
    }
  };

  const fetchParamMissions = async () => {
    try {
      const response = await api.get(`/orbit/${orbitId}/missions?param=${paramKey}`);
      setMissions(response.data.missions || []);
    } catch (error) {
      // Missions endpoint may not exist yet, that's ok
      console.log('Missions not available');
    }
  };

  const handleAddMission = async () => {
    if (!newMissionText.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/orbit/${orbitId}/missions`, {
        text: newMissionText.trim(),
        param: paramKey
      });
      toast.success(language === 'tr' ? 'Misyon eklendi!' : 'Mission added!');
      setNewMissionText('');
      setShowAddMission(false);
      fetchParamMissions();
    } catch (error) {
      toast.error(language === 'tr' ? 'Misyon eklenemedi' : 'Failed to add mission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleMission = async (mission) => {
    try {
      await api.patch(`/orbit/${orbitId}/missions/${mission.id}/toggle`);
      fetchParamMissions();
    } catch (error) {
      toast.error(language === 'tr' ? 'Güncelleme başarısız' : 'Update failed');
    }
  };

  const handleUpdateScore = async (newScore) => {
    try {
      await api.put(`/orbit/${orbitId}/scores`, {
        [paramKey]: newScore
      });
      setOrbit(prev => ({
        ...prev,
        orbit_scores: {
          ...prev.orbit_scores,
          [paramKey]: newScore
        }
      }));
      toast.success(language === 'tr' ? 'Skor güncellendi' : 'Score updated');
    } catch (error) {
      toast.error(language === 'tr' ? 'Güncelleme başarısız' : 'Update failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pb-36 lg:pb-24 px-4"
      style={{ position: 'relative', zIndex: 1, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.5rem)' }}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-6"
        >
          <button
            onClick={() => navigate(`/orbit/${orbitId}`)}
            className="text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <p className="text-[#A1A1AA] text-xs">{orbit?.project_name}</p>
            <h1 className="font-serif text-2xl text-[#F8F5F2] flex items-center gap-2">
              <span>{paramConfig.icon}</span>
              {paramInfo.name}
            </h1>
          </div>
        </motion.div>

        {/* Score Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-panel rounded-2xl p-6 mb-6"
          style={{ borderLeft: `4px solid ${paramConfig.color}` }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[#A1A1AA] text-sm mb-1">{paramInfo.description}</p>
              <div className="flex items-center gap-2">
                <span className="text-4xl font-serif" style={{ color: paramConfig.color }}>
                  {currentScore}
                </span>
                <span className="text-[#6B7280]">/10</span>
              </div>
            </div>
            
            {/* Score Adjuster */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleUpdateScore(Math.max(0, currentScore - 1))}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                disabled={currentScore <= 0}
              >
                <TrendingDown className="w-4 h-4 text-red-400" />
              </button>
              <button
                onClick={() => handleUpdateScore(Math.min(10, currentScore + 1))}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                disabled={currentScore >= 10}
              >
                <TrendingUp className="w-4 h-4 text-green-400" />
              </button>
            </div>
          </div>

          {/* Score Bar */}
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${currentScore * 10}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="h-full rounded-full"
              style={{ backgroundColor: paramConfig.color }}
            />
          </div>
        </motion.div>

        {/* Reflection Question */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="glass-panel rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <p className="text-xs uppercase tracking-wider text-[#A1A1AA]">
              {language === 'tr' ? 'Düşünme Sorusu' : 'Reflection Question'}
            </p>
          </div>
          <p className="text-[#D4AF37] font-serif text-lg mb-4">
            "{paramInfo.question}"
          </p>
          
          <div className="relative">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={language === 'tr' ? 'Düşüncelerinizi yazın...' : 'Write your thoughts...'}
              className="w-full px-4 py-3 pr-12 rounded-xl bg-black/40 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none text-sm resize-none"
              rows={3}
            />
            <div className="absolute right-2 top-2">
              <DictationButton 
                onTranscript={(text) => setNotes(prev => prev ? `${prev} ${text}` : text)}
                className="!p-1.5"
              />
            </div>
          </div>
        </motion.div>

        {/* Active Missions for this Parameter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-panel rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="font-sans text-[#F8F5F2]">
                {language === 'tr' ? 'Aktif Misyonlar' : 'Active Missions'}
              </h3>
            </div>
            <button
              onClick={() => setShowAddMission(true)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Mission List */}
          {missions.length > 0 ? (
            <div className="space-y-3">
              {missions.map((mission) => (
                <motion.div
                  key={mission.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                    mission.completed
                      ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30'
                      : 'bg-white/5 border-white/10'
                  }`}
                >
                  <button
                    onClick={() => handleToggleMission(mission)}
                    className="mt-0.5 flex-shrink-0"
                  >
                    {mission.completed ? (
                      <CheckCircle2 className="w-5 h-5 text-[#D4AF37]" />
                    ) : (
                      <Circle className="w-5 h-5 text-[#6B7280] hover:text-[#D4AF37]" />
                    )}
                  </button>
                  <p className={`text-sm font-sans ${
                    mission.completed ? 'text-[#A1A1AA] line-through' : 'text-[#F8F5F2]'
                  }`}>
                    {mission.text}
                  </p>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-[#6B7280] text-sm mb-2">
                {language === 'tr' ? 'Bu parametre için henüz misyon yok' : 'No missions for this parameter yet'}
              </p>
              <button
                onClick={() => setShowAddMission(true)}
                className="text-[#D4AF37] text-sm hover:underline"
              >
                {language === 'tr' ? 'İlk misyonu ekle →' : 'Add first mission →'}
              </button>
            </div>
          )}

          {/* Add Mission Panel */}
          <AnimatePresence>
            {showAddMission && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 pt-4 border-t border-white/10"
              >
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={newMissionText}
                      onChange={(e) => setNewMissionText(e.target.value)}
                      placeholder={language === 'tr' ? 'Misyonunu yaz...' : 'Write your mission...'}
                      className="w-full px-4 py-2 pr-10 rounded-lg bg-black/40 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddMission()}
                    />
                    <div className="absolute right-1 top-1">
                      <DictationButton 
                        onTranscript={(text) => setNewMissionText(prev => prev ? `${prev} ${text}` : text)}
                        className="!p-1.5"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleAddMission}
                    disabled={!newMissionText.trim() || submitting}
                    className="px-4 py-2 rounded-lg bg-[#D4AF37] text-[#030305] font-sans text-sm disabled:opacity-50"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === 'tr' ? 'Ekle' : 'Add')}
                  </button>
                  <button
                    onClick={() => setShowAddMission(false)}
                    className="p-2 text-[#6B7280] hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Tips / AI Suggestions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="glass-panel rounded-2xl p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <h3 className="font-sans text-[#F8F5F2]">
              {language === 'tr' ? 'İyileştirme İpuçları' : 'Improvement Tips'}
            </h3>
          </div>
          
          <div className="space-y-3">
            {currentScore < 4 && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-red-300 text-sm">
                  {language === 'tr' 
                    ? `${paramInfo.name} alanında kritik düşüklük. Bu, projenin başarısını engelliyor olabilir.`
                    : `Critical low in ${paramInfo.name}. This may be blocking project success.`}
                </p>
              </div>
            )}
            {currentScore >= 4 && currentScore < 7 && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                <p className="text-amber-300 text-sm">
                  {language === 'tr' 
                    ? `${paramInfo.name} iyileştirme gerektiriyor. Küçük değişiklikler büyük fark yaratabilir.`
                    : `${paramInfo.name} needs improvement. Small changes can make a big difference.`}
                </p>
              </div>
            )}
            {currentScore >= 7 && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                <p className="text-green-300 text-sm">
                  {language === 'tr' 
                    ? `${paramInfo.name} güçlü durumda. Bu avantajı koruyun ve diğer alanlara odaklanın.`
                    : `${paramInfo.name} is strong. Maintain this advantage and focus on other areas.`}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
      
      <BottomNav />
    </div>
  );
};

export default OrbitMissionControl;
